import { apiClient } from "@/services/apiClient";

/**
 * The professional second factor (spec 5d9018f9).
 *
 * Enrollment and step-up only. The LOGIN half lives in authService, because
 * those calls happen before a session exists and go through a different
 * client path.
 */

export interface TotpStatus {
  enrolled: boolean;
  /**
   * The SERVER's verdict on whether this professional is blocked right now.
   *
   * Not derivable here: enforcement is env-driven on the backend and defaults
   * off, so a client that blocked on `!enrolled` would lock out every
   * un-enrolled professional while the capability was still shipping. Same
   * two terms as TotpEnrollmentGuard, decided in one place.
   */
  enrollmentRequired: boolean;
  unusedRecoveryCodes: number;
  /** How long a verification counts for a sensitive action. */
  stepUpGraceMinutes: number;
}

export interface TotpEnrollmentStart {
  /** The base32 seed, for manual entry when a QR cannot be scanned. */
  secret: string;
  /** The otpauth:// URI a QR encodes. */
  otpauthUri: string;
}

export function getTotpStatus(): Promise<TotpStatus> {
  return apiClient.get<TotpStatus>("/auth/me/totp/status").then((r) => r.data);
}

/**
 * Starts enrollment and returns the seed.
 *
 * The seed is returned ONCE and never retrievable again — abandoning this
 * screen means starting over, which the server supports by replacing an
 * unconfirmed enrollment rather than refusing a second attempt.
 */
export function startTotpEnrollment(): Promise<TotpEnrollmentStart> {
  return apiClient
    .post<TotpEnrollmentStart>("/auth/me/totp/enroll")
    .then((r) => r.data);
}

/**
 * Confirms enrollment and returns the recovery codes.
 *
 * THE ONLY TIME the codes are ever available. There is no endpoint that
 * returns them again; a professional who loses them re-enrolls or asks an
 * admin to reset.
 */
export function confirmTotpEnrollment(code: string): Promise<{ recoveryCodes: string[] }> {
  return apiClient
    .post<{ recoveryCodes: string[] }>("/auth/me/totp/confirm", {
      code: code.replace(/\s/g, ""),
    })
    .then((r) => r.data);
}

/** Re-verifies the current session before a sensitive action. 204, no body. */
export function stepUpTotp(code: string): Promise<void> {
  return apiClient
    .post("/auth/me/totp/step-up", { code: code.replace(/\s/g, "") })
    .then(() => undefined);
}

// The contract lives in totp-contract.ts so it can be loaded without this
// module's apiClient import; re-exported here for call sites.
export {
  TOTP_ENROLLMENT_REQUIRED,
  TOTP_STEP_UP_REQUIRED,
  isTotpEnrollmentRequiredError,
  isTotpStepUpRequiredError,
} from "@/services/totp-contract";
